import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CoverImage from './CoverImage';
import StoryModeTour from './StoryModeTour';
import '../styles/SubjectExplorerPage.css';
import { trackEvent } from '../services/analytics';
import {
  addToReadingList,
  downloadTextFile,
  generateBibtex,
  generateRis,
  loadReadingList,
} from '../services/readingList';
import {
  enrichSubjectMetadata,
  LEGACY_SUBJECT_FALLBACK_CODE,
  localizedSubjectLabel,
  normalizeSubjectSearch,
  SUBJECT_CATALOG,
  parseSubjectJsonResponse,
} from '../services/subjectCatalog';

const PAGE_SIZE = 24;
const FILTER_PREF_PREFIX = 'opac_subject_filters_';
const FOLLOWED_SUBJECTS_KEY = 'opac_followed_subjects';

const SORT_OPTIONS = {
  relevance: 'Relevance',
  availability: 'Availability',
  newest: 'Newest',
  title_asc: 'Title A-Z',
  title_desc: 'Title Z-A',
  most_borrowed: 'Most borrowed',
  recently_added: 'Recently added',
};

const SUBJECT_DDC_RANGE = {
  'computer-science-information':  '0',
  'philosophy-psychology':         '1',
  'religion-culture':              '2',
  'social-sciences-management':    '3',
  'language-linguistics':          '4',
  'mathematics-natural-sciences':  '5',
  'engineering-applied-technology':'6',
  'arts-design-recreation':        '7',
  'literature-communication':      '8',
  'history-geography-biography':   '9',
};


const buildLegacySubjectQuery = (baseQuery = '', userQuery = '') => {
  const tokenize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && token !== 'or' && token !== 'and');

  const mergedTokens = [...tokenize(userQuery), ...tokenize(baseQuery)];
  return Array.from(new Set(mergedTokens)).slice(0, 12).join(' ');
};

const buildLocalSubjectFacets = (rows = []) => {
  const facets = {
    availability: { available: 0, unavailable: 0 },
    years: {},
  };

  rows.forEach((row) => {
    if ((Number(row.available_count) || 0) > 0) {
      facets.availability.available += 1;
    } else {
      facets.availability.unavailable += 1;
    }

    const year = String(row.copyrightdate || '').trim();
    if (/^\d{4}$/.test(year)) {
      facets.years[year] = (facets.years[year] || 0) + 1;
    }
  });

  return facets;
};

const buildCsv = (rows = []) => {
  const headers = ['Biblio ID', 'Title', 'Author', 'Year', 'Edition', 'Call Number', 'Available Count', 'Total Items'];
  const esc = (value) => {
    const raw = String(value == null ? '' : value);
    if (/[",\n]/.test(raw)) {
      return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
  };

  const lines = rows.map((book) => [
    book.biblio_id,
    book.title,
    book.author,
    book.copyrightdate,
    book.editionstatement,
    book.call_number,
    book.available_count,
    book.item_count,
  ].map(esc).join(','));

  return [headers.join(','), ...lines].join('\n');
};

const getMatchReason = (book, query) => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) {
    return '';
  }

  if (String(book.title || '').toLowerCase().includes(needle)) {
    return 'Matched in title';
  }
  if (String(book.author || '').toLowerCase().includes(needle)) {
    return 'Matched in author';
  }
  if (String(book.isbn || '').toLowerCase().includes(needle)) {
    return 'Matched in ISBN';
  }
  return 'Related match';
};

function SubjectExplorerPage() {
  const navigate = useNavigate();
  const { subjectKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentQuery = searchParams.get('q') || '';
  const currentPage = Math.max(Number(searchParams.get('page') || 1), 1);
  const currentSort = ['relevance', 'availability', 'newest', 'title_asc', 'title_desc', 'most_borrowed', 'recently_added'].includes(searchParams.get('sort') || '')
    ? searchParams.get('sort')
    : 'relevance';
  const currentAvailability = ['all', 'available', 'unavailable'].includes(searchParams.get('availability') || '')
    ? searchParams.get('availability')
    : 'all';
  const currentYear = /^\d{4}$/.test(searchParams.get('year') || '') ? searchParams.get('year') : '';

  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [subjectsError, setSubjectsError] = useState(null);
  const [legacyMode, setLegacyMode] = useState(false);
  const [booksLoading, setBooksLoading] = useState(false);
  const [error, setError] = useState(null);
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchInput, setSearchInput] = useState(currentQuery);
  const [facets, setFacets] = useState({ availability: { available: 0, unavailable: 0 }, years: {} });
  const [stats, setStats] = useState({ totalTitles: 0, availableNow: 0, oldestYear: null, newestYear: null });
  const [community, setCommunity] = useState({ mostBorrowedThisMonth: [], mostViewedThisWeek: [] });
  const [recommendations, setRecommendations] = useState({ subjects: [] });
  const [followedArrivals, setFollowedArrivals] = useState({ bySubject: {} });
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const [jumpInput, setJumpInput] = useState('');
  const [labelLocale, setLabelLocale] = useState('en');
  const [savedBookIds, setSavedBookIds] = useState(() => new Set(loadReadingList().map((book) => String(book.biblio_id))));
  const [followedSubjects, setFollowedSubjects] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(FOLLOWED_SUBJECTS_KEY) || '[]');
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (_error) {
      return new Set();
    }
  });
  const resultsRef = useRef(null);
  const firstResultRef = useRef(null);
  const pendingResultScrollRef = useRef(false);
  const prefetchCacheRef = useRef(new Map());
  const zeroStateTrackedRef = useRef('');

  const scrollResultIntoView = (target) => {
    if (!target) {
      return;
    }

    const header = document.querySelector('.institute-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const extraGap = 12;
    const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - extraGap;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  useEffect(() => {
    let ignore = false;

    const fetchSubjects = async () => {
      try {
        setSubjectsLoading(true);
        setSubjectsError(null);
        setLegacyMode(false);
        const response = await fetch('/api/subjects');
        const data = await parseSubjectJsonResponse(response, 'Unable to fetch subjects.');
        if (!ignore) {
          setSubjects(Array.isArray(data.subjects) ? data.subjects.map(enrichSubjectMetadata) : []);
        }
      } catch (fetchError) {
        if (!ignore) {
          if (fetchError.code === LEGACY_SUBJECT_FALLBACK_CODE) {
            setLegacyMode(true);
            setSubjects(SUBJECT_CATALOG.map((subject) => ({ ...enrichSubjectMetadata(subject), total: 0 })));
            setSubjectsError('Running in compatibility mode using existing subject search.');
          } else {
            setSubjects([]);
            setSubjectsError(fetchError.message || 'Unable to load subject shelves right now.');
          }
        }
      } finally {
        if (!ignore) {
          setSubjectsLoading(false);
        }
      }
    };

    trackEvent('subject_explorer_page_viewed', { source: 'subject-page', subject: subjectKey || null });
    fetchSubjects();

    return () => {
      ignore = true;
    };
  }, [subjectKey]);

  useEffect(() => {
    setSearchInput(currentQuery);
  }, [currentQuery]);

  const subjectFromList = useMemo(
    () => subjects.find((subject) => subject.key === subjectKey) || null,
    [subjects, subjectKey]
  );

  const catalogSubjects = useMemo(
    () => (subjects.length ? subjects : SUBJECT_CATALOG.map(enrichSubjectMetadata)),
    [subjects]
  );

  const relatedSubjects = useMemo(() => {
    const index = catalogSubjects.findIndex((subject) => subject.key === subjectKey);
    if (index < 0) {
      return { broader: null, narrower: [], similar: [] };
    }

    const broader = catalogSubjects[index - 1] || null;
    const narrower = [catalogSubjects[index + 1], catalogSubjects[index + 2]].filter(Boolean);
    const similar = catalogSubjects
      .filter((subject) => subject.key !== subjectKey)
      .slice(Math.max(0, index - 2), index + 3)
      .filter((subject) => subject.key !== subjectKey)
      .slice(0, 4);

    return { broader, narrower, similar };
  }, [catalogSubjects, subjectKey]);

  const jumpSuggestions = useMemo(() => {
    const needle = normalizeSubjectSearch(jumpInput);
    if (!needle) {
      return catalogSubjects.slice(0, 6);
    }
    return catalogSubjects
      .filter((subject) => {
        const haystack = normalizeSubjectSearch([
          subject.label,
          subject.labelHi,
          subject.labelTe,
          subject.key,
          ...(subject.aliases || []),
        ].join(' '));
        return haystack.includes(needle);
      })
      .slice(0, 6);
  }, [catalogSubjects, jumpInput]);

  const activeFilters = useMemo(() => {
    const chips = [];
    if (currentQuery) chips.push({ type: 'query', label: `Query: ${currentQuery}` });
    if (currentSort !== 'relevance') chips.push({ type: 'sort', label: `Sort: ${SORT_OPTIONS[currentSort] || currentSort}` });
    if (currentAvailability !== 'all') chips.push({ type: 'availability', label: `Availability: ${currentAvailability}` });
    if (currentYear) chips.push({ type: 'year', label: `Year: ${currentYear}` });
    return chips;
  }, [currentAvailability, currentQuery, currentSort, currentYear]);

  const nearbyYears = useMemo(() => {
    if (!currentYear) {
      return [];
    }
    const base = Number(currentYear);
    const candidates = [base - 2, base - 1, base + 1, base + 2]
      .filter((year) => (!stats.oldestYear || year >= stats.oldestYear) && (!stats.newestYear || year <= stats.newestYear));
    return Array.from(new Set(candidates.map(String)));
  }, [currentYear, stats.oldestYear, stats.newestYear]);

  useEffect(() => {
    if (!subjectKey) {
      return;
    }

    const hasExplicitFilter = searchParams.has('sort') || searchParams.has('availability') || searchParams.has('year');
    if (hasExplicitFilter) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(`${FILTER_PREF_PREFIX}${subjectKey}`);
      if (!raw) {
        return;
      }
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== 'object') {
        return;
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (saved.sort && saved.sort !== 'relevance') next.set('sort', saved.sort);
        if (saved.availability && saved.availability !== 'all') next.set('availability', saved.availability);
        if (saved.year) next.set('year', saved.year);
        return next;
      }, { replace: true });
    } catch (_error) {
      // Ignore storage parse issues.
    }
  }, [searchParams, setSearchParams, subjectKey]);

  useEffect(() => {
    if (!subjectKey) {
      return;
    }
    try {
      sessionStorage.setItem(`${FILTER_PREF_PREFIX}${subjectKey}`, JSON.stringify({
        sort: currentSort,
        availability: currentAvailability,
        year: currentYear,
      }));
    } catch (_error) {
      // Ignore storage write failures.
    }
  }, [currentAvailability, currentSort, currentYear, subjectKey]);

  useEffect(() => {
    let ignore = false;

    const fetchBooks = async () => {
      if (!subjectKey) {
        setBooks([]);
        setSelectedSubject(null);
        setTotal(0);
        setError(null);
        setStats({ totalTitles: 0, availableNow: 0, oldestYear: null, newestYear: null });
        setCommunity({ mostBorrowedThisMonth: [], mostViewedThisWeek: [] });
        return;
      }

      try {
        setBooksLoading(true);
        setError(null);

        const offset = (currentPage - 1) * PAGE_SIZE;
        const cacheKey = JSON.stringify({
          subjectKey,
          currentQuery,
          currentPage,
          currentSort,
          currentAvailability,
          currentYear,
          legacyMode,
        });
        const cached = prefetchCacheRef.current.get(cacheKey);
        if (cached) {
          setBooks(Array.isArray(cached.books) ? cached.books : []);
          setTotal(Number(cached.total) || 0);
          setSelectedSubject(cached.subject || subjectFromList || null);
          setFacets(cached.facets || buildLocalSubjectFacets(cached.books || []));
          setStats(cached.stats || { totalTitles: Number(cached.total) || 0, availableNow: 0, oldestYear: null, newestYear: null });
          setCommunity(cached.community || { mostBorrowedThisMonth: [], mostViewedThisWeek: [] });
          setBooksLoading(false);
          return;
        }

        let data;

        if (legacyMode) {
          const selected = subjects.find((subject) => subject.key === subjectKey);
          const mergedQuery = buildLegacySubjectQuery(selected?.query || '', currentQuery);

          const response = await fetch('/api/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query: mergedQuery,
              type: 'subject',
              limit: PAGE_SIZE,
              offset,
            }),
          });

          data = await parseSubjectJsonResponse(response, 'Unable to fetch books for this subject.');
          data.subject = selected || null;
          data.facets = buildLocalSubjectFacets(data.books || []);
          data.stats = {
            totalTitles: Number(data.total) || 0,
            availableNow: (data.books || []).filter((book) => (Number(book.available_count) || 0) > 0).length,
            oldestYear: null,
            newestYear: null,
          };
          data.community = { mostBorrowedThisMonth: [], mostViewedThisWeek: [] };
        } else {
          const url = `/api/subjects/${subjectKey}/books?limit=${PAGE_SIZE}&offset=${offset}&query=${encodeURIComponent(currentQuery)}&sort=${encodeURIComponent(currentSort)}&availability=${encodeURIComponent(currentAvailability)}&year=${encodeURIComponent(currentYear)}`;
          const response = await fetch(url);
          data = await parseSubjectJsonResponse(response, 'Unable to fetch books for this subject.');
        }

        if (!ignore) {
          prefetchCacheRef.current.set(cacheKey, data);
          setBooks(Array.isArray(data.books) ? data.books : []);
          setTotal(Number(data.total) || 0);
          setSelectedSubject(data.subject || subjectFromList || null);
          setFacets(data.facets || buildLocalSubjectFacets(data.books || []));
          setStats(data.stats || { totalTitles: Number(data.total) || 0, availableNow: 0, oldestYear: null, newestYear: null });
          setCommunity(data.community || { mostBorrowedThisMonth: [], mostViewedThisWeek: [] });
        }
      } catch (fetchError) {
        if (!ignore) {
          setBooks([]);
          setTotal(0);
          setSelectedSubject(null);
          setError(fetchError.message || 'Unable to load books for this subject right now.');
          setCommunity({ mostBorrowedThisMonth: [], mostViewedThisWeek: [] });
        }
      } finally {
        if (!ignore) {
          setBooksLoading(false);
        }
      }
    };

    fetchBooks();

    return () => {
      ignore = true;
    };
  }, [subjectKey, currentQuery, currentPage, currentSort, currentAvailability, currentYear, legacyMode, subjects, subjectFromList]);

  useEffect(() => {
    let ignore = false;
    if (!subjectKey || legacyMode) {
      setRecommendations({ subjects: [] });
      return () => {
        ignore = true;
      };
    }

    const fetchRecommendations = async () => {
      try {
        const response = await fetch(`/api/subjects/${subjectKey}/recommendations`);
        const data = await parseSubjectJsonResponse(response, 'Unable to load recommendations.');
        if (!ignore) {
          setRecommendations({ subjects: Array.isArray(data.subjects) ? data.subjects : [] });
        }
      } catch (_error) {
        if (!ignore) {
          setRecommendations({ subjects: [] });
        }
      }
    };

    fetchRecommendations();
    return () => {
      ignore = true;
    };
  }, [legacyMode, subjectKey]);

  useEffect(() => {
    let ignore = false;
    const followedKeys = Array.from(followedSubjects);
    if (!followedKeys.length || legacyMode) {
      setFollowedArrivals({ bySubject: {} });
      return () => {
        ignore = true;
      };
    }

    const fetchFollowedArrivals = async () => {
      try {
        const queryKeys = encodeURIComponent(followedKeys.join(','));
        const response = await fetch(`/api/subjects/followed/new-arrivals?keys=${queryKeys}&limitPerSubject=3`);
        const data = await parseSubjectJsonResponse(response, 'Unable to load followed subject updates.');
        if (!ignore) {
          setFollowedArrivals({ bySubject: data.bySubject || {} });
        }
      } catch (_error) {
        if (!ignore) {
          setFollowedArrivals({ bySubject: {} });
        }
      }
    };

    fetchFollowedArrivals();
    return () => {
      ignore = true;
    };
  }, [followedSubjects, legacyMode]);

  useEffect(() => {
    if (booksLoading || error) {
      return;
    }
    setLiveAnnouncement(`Updated results: ${total.toLocaleString()} titles. Current page ${currentPage} of ${Math.max(Math.ceil(total / PAGE_SIZE), 1)}.`);
  }, [booksLoading, currentAvailability, currentPage, currentQuery, currentSort, currentYear, error, total]);

  useEffect(() => {
    if (booksLoading || error || books.length > 0) {
      return;
    }

    const signature = `${subjectKey}|${currentQuery}|${currentSort}|${currentAvailability}|${currentYear}`;
    if (zeroStateTrackedRef.current === signature) {
      return;
    }
    zeroStateTrackedRef.current = signature;

    trackEvent('subject_zero_results_seen', {
      subject: subjectKey,
      query: currentQuery,
      sort: currentSort,
      availability: currentAvailability,
      year: currentYear || null,
    });
  }, [books, booksLoading, currentAvailability, currentQuery, currentSort, currentYear, error, subjectKey]);

  useEffect(() => {
    if (!pendingResultScrollRef.current || !books.length) {
      return;
    }

    pendingResultScrollRef.current = false;
    scrollResultIntoView(firstResultRef.current || resultsRef.current);
  }, [books, currentPage]);

  useEffect(() => {
    if (legacyMode || !subjectKey || booksLoading) {
      return;
    }

    const nextPage = currentPage + 1;
    const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
    if (nextPage > totalPages) {
      return;
    }

    const nextOffset = (nextPage - 1) * PAGE_SIZE;
    const nextCacheKey = JSON.stringify({
      subjectKey,
      currentQuery,
      currentPage: nextPage,
      currentSort,
      currentAvailability,
      currentYear,
      legacyMode,
    });

    if (prefetchCacheRef.current.has(nextCacheKey)) {
      return;
    }

    const url = `/api/subjects/${subjectKey}/books?limit=${PAGE_SIZE}&offset=${nextOffset}&query=${encodeURIComponent(currentQuery)}&sort=${encodeURIComponent(currentSort)}&availability=${encodeURIComponent(currentAvailability)}&year=${encodeURIComponent(currentYear)}`;
    fetch(url)
      .then((response) => parseSubjectJsonResponse(response, 'Unable to prefetch next page.'))
      .then((data) => {
        prefetchCacheRef.current.set(nextCacheKey, data);
      })
      .catch(() => {
        // Prefetch failures should stay silent.
      });
  }, [subjectKey, currentQuery, currentPage, currentSort, currentAvailability, currentYear, total, booksLoading, legacyMode]);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const updateParams = ({
    query = currentQuery,
    page = 1,
    sort = currentSort,
    availability = currentAvailability,
    year = currentYear,
  }) => {
    const nextParams = {};
    if (query) {
      nextParams.q = query;
    }
    if (page > 1) {
      nextParams.page = String(page);
    }
    if (sort && sort !== 'relevance') {
      nextParams.sort = sort;
    }
    if (availability && availability !== 'all') {
      nextParams.availability = availability;
    }
    if (year) {
      nextParams.year = year;
    }
    setSearchParams(nextParams);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    trackEvent('subject_search_submitted', {
      subject: subjectKey,
      queryLength: searchInput.trim().length,
      source: 'subject-page',
    });
    updateParams({ query: searchInput.trim(), page: 1 });
  };

  const handleFilterChange = (filterName, value, updater) => {
    trackEvent('subject_filter_changed', {
      subject: subjectKey,
      filter: filterName,
      value,
      totalVisible: total,
    });
    updater();
  };

  const removeFilter = (filterType) => {
    if (filterType === 'query') {
      setSearchInput('');
      updateParams({ query: '', page: 1 });
      return;
    }
    if (filterType === 'sort') {
      updateParams({ sort: 'relevance', page: 1 });
      return;
    }
    if (filterType === 'availability') {
      updateParams({ availability: 'all', page: 1 });
      return;
    }
    if (filterType === 'year') {
      updateParams({ year: '', page: 1 });
    }
  };

  const clearAllFilters = () => {
    trackEvent('subject_filters_cleared', { subject: subjectKey });
    setSearchInput('');
    updateParams({ query: '', page: 1, sort: 'relevance', availability: 'all', year: '' });
  };

  const toggleFollowSubject = () => {
    const next = new Set(followedSubjects);
    const currentlyFollowed = next.has(subjectKey);
    if (currentlyFollowed) {
      next.delete(subjectKey);
    } else {
      next.add(subjectKey);
    }
    setFollowedSubjects(next);
    try {
      localStorage.setItem(FOLLOWED_SUBJECTS_KEY, JSON.stringify(Array.from(next)));
    } catch (_error) {
      // Ignore storage errors.
    }
    trackEvent('subject_follow_toggled', {
      subject: subjectKey,
      followed: !currentlyFollowed,
    });
  };

  const exportBooks = (format) => {
    if (!books.length) {
      return;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    const baseName = `${subjectKey || 'subject'}-${stamp}`;
    if (format === 'ris') {
      downloadTextFile(`${baseName}.ris`, generateRis(books), 'application/x-research-info-systems');
    } else if (format === 'bibtex') {
      downloadTextFile(`${baseName}.bib`, generateBibtex(books), 'application/x-bibtex');
    } else {
      downloadTextFile(`${baseName}.csv`, buildCsv(books), 'text/csv');
    }

    trackEvent('subject_export_triggered', {
      subject: subjectKey,
      format,
      count: books.length,
    });
  };

  const handleAddToReadingList = (book) => {
    addToReadingList(book);
    setSavedBookIds((prev) => {
      const next = new Set(prev);
      next.add(String(book.biblio_id));
      return next;
    });
    trackEvent('subject_add_to_reading_list', {
      subject: subjectKey,
      biblioId: book.biblio_id,
    });
  };

  const handleBookOpen = (book) => {
    trackEvent('book_detail_opened', {
      from: 'subject-explorer',
      biblioId: book.biblio_id,
      subject: subjectKey,
    });
    navigate(`/book/${book.biblio_id}`, { 
      state: { 
        from: 'subject-explorer',
        subjectKey,
        subjectQuery: currentQuery,
        subjectPage: currentPage,
        subjectSort: currentSort,
        subjectAvailability: currentAvailability,
        subjectYear: currentYear,
      } 
    });
  };

  const subjectMissing = !subjectsLoading && Boolean(subjectKey) && !subjectFromList && !legacyMode;
  const lowAvailabilityRatio = total > 0 ? Number((stats.availableNow || 0)) / total : 0;

  useEffect(() => {
    if (!booksLoading && total >= 20 && lowAvailabilityRatio > 0 && lowAvailabilityRatio < 0.2) {
      trackEvent('subject_low_availability_signal', {
        subject: subjectKey,
        total,
        availableNow: stats.availableNow,
      });
    }
  }, [booksLoading, lowAvailabilityRatio, stats.availableNow, subjectKey, total]);

  return (
    <section className="subject-page">
      <div className="subject-page__hero">
        <div>
          <p className="subject-page__eyebrow">Subject Explorer</p>
          <h1>{localizedSubjectLabel(selectedSubject || subjectFromList || {}, labelLocale) || 'Subject Shelf'}</h1>
          <div className="subject-page__jump-wrap">
            <label htmlFor="subject-jump" className="subject-page__jump-label">Jump to subject</label>
            <input
              id="subject-jump"
              type="text"
              className="subject-page__jump-input"
              placeholder="Type subject name"
              value={jumpInput}
              onChange={(event) => setJumpInput(event.target.value)}
            />
            <div className="subject-page__jump-list">
              {jumpSuggestions.map((subject) => (
                <button
                  key={subject.key}
                  type="button"
                  className="subject-page__jump-item"
                  onClick={() => navigate(`/subjects/${subject.key}`)}
                >
                  {localizedSubjectLabel(subject, labelLocale)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="subject-page__hero-actions">
          {SUBJECT_DDC_RANGE[subjectKey] && (
  <button
    type="button"
    className="subject-page__explore-ddc-btn"
    onClick={() => navigate('/discover/' + SUBJECT_DDC_RANGE[subjectKey])}
  >
    Explore sub-topics →
  </button>
)}

          <div className="subject-page__locale-switch" role="group" aria-label="Subject label language">
            <button type="button" className={labelLocale === 'en' ? 'is-active' : ''} onClick={() => setLabelLocale('en')}>EN</button>
            <button type="button" className={labelLocale === 'hi' ? 'is-active' : ''} onClick={() => setLabelLocale('hi')}>HI</button>
            <button type="button" className={labelLocale === 'te' ? 'is-active' : ''} onClick={() => setLabelLocale('te')}>TE</button>
          </div>
          <button type="button" className="subject-page__follow-btn" onClick={toggleFollowSubject}>
            {followedSubjects.has(subjectKey) ? 'Following subject' : 'Follow subject'}
          </button>
          <button type="button" className="subject-page__home-btn" onClick={() => navigate('/subjects')}>
            All Subjects
          </button>
        </div>
      </div>

      <div className="subject-breadcrumb">
        <button type="button" className="subject-breadcrumb__link" onClick={() => navigate('/subjects')}>
          Subject shelves
        </button>
        <span className="subject-breadcrumb__sep">/</span>
        <span className="subject-breadcrumb__current">{localizedSubjectLabel(selectedSubject || subjectFromList || {}, labelLocale) || subjectKey}</span>
      </div>

      <div className="subject-page__layout">
        <div className="subject-page__results">
          {subjectsError && !subjectsLoading && <div className="subject-page__error">{subjectsError}</div>}

          {subjectMissing && (
            <div className="subject-page__state">Subject not found. Please choose from the subject shelf directory.</div>
          )}

          {!subjectMissing && (
            <>
              <div className="subject-results__header">
                <div>
                  <p className="subject-results__eyebrow">Current shelf</p>
                </div>
                <div className="subject-results__meta">
                  <span>{total.toLocaleString()} matching titles</span>
                  <span>Available now: {Number(stats.availableNow || 0).toLocaleString()}</span>
                  {(stats.oldestYear || stats.newestYear) && (
                    <span>
                      Year range: {stats.oldestYear || 'N/A'} - {stats.newestYear || 'N/A'}
                    </span>
                  )}
                  {currentQuery && <span>Filtered by “{currentQuery}”</span>}
                </div>
              </div>

              <div className="subject-related-panel">
                <div>
                  <h3>Related subjects</h3>
                  <div className="subject-related-panel__chips">
                    {relatedSubjects.similar.map((subject) => (
                      <button
                        key={subject.key}
                        type="button"
                        className="subject-related-panel__chip"
                        onClick={() => navigate(`/subjects/${subject.key}`)}
                      >
                        {localizedSubjectLabel(subject, labelLocale)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="subject-related-panel__taxonomy">
                  <span>Broader: {relatedSubjects.broader ? <button type="button" onClick={() => navigate(`/subjects/${relatedSubjects.broader.key}`)}>{localizedSubjectLabel(relatedSubjects.broader, labelLocale)}</button> : 'N/A'}</span>
                  <span>
                    Narrower:
                    {relatedSubjects.narrower.length
                      ? relatedSubjects.narrower.map((subject) => (
                        <button key={subject.key} type="button" onClick={() => navigate(`/subjects/${subject.key}`)}>
                          {localizedSubjectLabel(subject, labelLocale)}
                        </button>
                      ))
                      : ' N/A'}
                  </span>
                </div>
              </div>

              <div className="subject-followed-dashboard">
                <h4>Your followed subjects: new arrivals</h4>
                {Object.keys(followedArrivals.bySubject || {}).length === 0 && (
                  <p>Follow subjects to get a mini dashboard of fresh arrivals here.</p>
                )}
                {Object.entries(followedArrivals.bySubject || {}).map(([key, entries]) => {
                  const subjectMeta = catalogSubjects.find((subject) => subject.key === key) || { key, label: key };
                  return (
                    <div key={key} className="subject-followed-dashboard__group">
                      <button type="button" className="subject-followed-dashboard__title" onClick={() => navigate(`/subjects/${key}`)}>
                        {localizedSubjectLabel(subjectMeta, labelLocale)}
                      </button>
                      <ul>
                        {entries.map((book) => (
                          <li key={`${key}-${book.biblio_id}`}>
                            <button type="button" onClick={() => handleBookOpen(book)}>{book.title}</button>
                            <span>{String(book.latest_accession_date || '').slice(0, 10)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              <div className="subject-readers-explored">
                <h4>Readers also explored</h4>
                <div className="subject-readers-explored__chips">
                  {(recommendations.subjects || []).map((subject) => (
                    <button
                      key={subject.key}
                      type="button"
                      onClick={() => navigate(`/subjects/${subject.key}`)}
                    >
                      {localizedSubjectLabel(subject, labelLocale)}
                      <span>{subject.score}</span>
                    </button>
                  ))}
                  {!(recommendations.subjects || []).length && <p>No recommendation signal yet. Explore nearby shelves.</p>}
                </div>
              </div>

              <StoryModeTour subject={selectedSubject || subjectFromList} books={books} />

              <div className="subject-controls">
                <p className="subject-sr-live" aria-live="polite" role="status">{liveAnnouncement}</p>
                <form className="subject-search" onSubmit={handleSearchSubmit}>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search by title, author or ISBN within this subject"
                    className="subject-search__input"
                  />
                  <button type="submit" className="subject-search__button" disabled={booksLoading}>
                    {booksLoading ? 'Searching...' : 'Search Shelf'}
                  </button>
                </form>

                <div className="subject-facets">
                  <label>
                    Sort
                    <select
                      value={currentSort}
                      onChange={(event) => handleFilterChange('sort', event.target.value, () => updateParams({ sort: event.target.value, page: 1 }))}
                      disabled={booksLoading || legacyMode}
                    >
                      <option value="relevance">Relevance</option>
                      <option value="availability">Availability</option>
                      <option value="newest">Newest</option>
                      <option value="title_asc">Title A-Z</option>
                      <option value="title_desc">Title Z-A</option>
                      <option value="most_borrowed">Most borrowed</option>
                      <option value="recently_added">Recently added</option>
                    </select>
                  </label>

                  <label>
                    Availability
                    <select
                      value={currentAvailability}
                      onChange={(event) => handleFilterChange('availability', event.target.value, () => updateParams({ availability: event.target.value, page: 1 }))}
                      disabled={booksLoading || legacyMode}
                    >
                      <option value="all">All ({total})</option>
                      <option value="available">Available ({facets.availability?.available || 0})</option>
                      <option value="unavailable">Unavailable ({facets.availability?.unavailable || 0})</option>
                    </select>
                  </label>

                  <label>
                    Year
                    <select
                      value={currentYear}
                      onChange={(event) => handleFilterChange('year', event.target.value, () => updateParams({ year: event.target.value, page: 1 }))}
                      disabled={booksLoading || legacyMode}
                    >
                      <option value="">All years</option>
                      {Object.entries(facets.years || {})
                        .sort((a, b) => Number(b[0]) - Number(a[0]))
                        .map(([year, count]) => (
                          <option key={year} value={year}>{year} ({count})</option>
                        ))}
                    </select>
                  </label>
                  <div className="subject-facets__summary" aria-live="polite">
                    {booksLoading ? 'Updating filters...' : `${total.toLocaleString()} titles after filters`}
                  </div>
                </div>

                <div className="subject-active-filters">
                  <div className="subject-active-filters__chips">
                    {activeFilters.length === 0 && <span className="subject-active-filters__empty">No active filters</span>}
                    {activeFilters.map((filter) => (
                      <button
                        key={filter.type}
                        type="button"
                        className="subject-active-filters__chip"
                        onClick={() => removeFilter(filter.type)}
                        aria-label={`Remove ${filter.label}`}
                      >
                        {filter.label} x
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="subject-active-filters__clear"
                    onClick={clearAllFilters}
                    disabled={activeFilters.length === 0}
                  >
                    Clear all
                  </button>
                </div>
              </div>

              <div className="subject-community-proof">
                <div>
                  <h4>Most borrowed this month</h4>
                  <ul>
                    {(community.mostBorrowedThisMonth || []).map((item) => (
                      <li key={`borrow-${item.biblio_id}`}>
                        <button type="button" onClick={() => handleBookOpen(item)}>{item.title}</button>
                        <span>{item.borrow_count} borrows</span>
                      </li>
                    ))}
                    {!(community.mostBorrowedThisMonth || []).length && <li>No borrowing trend data yet.</li>}
                  </ul>
                </div>
                <div>
                  <h4>Most viewed this week</h4>
                  <ul>
                    {(community.mostViewedThisWeek || []).map((item) => (
                      <li key={`view-${item.biblio_id}`}>
                        <button type="button" onClick={() => handleBookOpen(item)}>{item.title}</button>
                        <span>{item.view_count} views</span>
                      </li>
                    ))}
                    {!(community.mostViewedThisWeek || []).length && <li>No view trend data yet.</li>}
                  </ul>
                </div>
              </div>

              <div className="subject-export-actions">
                <span>Bulk export current page:</span>
                <button type="button" onClick={() => exportBooks('ris')} disabled={!books.length}>RIS</button>
                <button type="button" onClick={() => exportBooks('bibtex')} disabled={!books.length}>BibTeX</button>
                <button type="button" onClick={() => exportBooks('csv')} disabled={!books.length}>CSV</button>
              </div>

              {error && <div className="subject-page__error">{error}</div>}
              {booksLoading && <div className="subject-page__state">Loading books...</div>}

              {!booksLoading && !error && books.length === 0 && (
                <div className="subject-page__state subject-page__state--zero">
                  <p>No books found for this subject and filter combination.</p>
                  <div className="subject-page__state-actions">
                    {currentYear && <button type="button" onClick={() => updateParams({ year: '', page: 1 })}>Remove year filter</button>}
                    {currentAvailability !== 'all' && <button type="button" onClick={() => updateParams({ availability: 'all', page: 1 })}>Show all availability</button>}
                    {currentQuery && <button type="button" onClick={() => { setSearchInput(''); updateParams({ query: '', page: 1 }); }}>Clear search query</button>}
                    <button type="button" onClick={clearAllFilters}>Reset all filters</button>
                  </div>
                  {nearbyYears.length > 0 && (
                    <div className="subject-page__state-suggestions">
                      <span>Try nearby years:</span>
                      {nearbyYears.map((year) => (
                        <button key={year} type="button" onClick={() => updateParams({ year, page: 1 })}>{year}</button>
                      ))}
                    </div>
                  )}
                  {relatedSubjects.similar.length > 0 && (
                    <div className="subject-page__state-suggestions">
                      <span>Try similar subjects:</span>
                      {relatedSubjects.similar.map((subject) => (
                        <button key={subject.key} type="button" onClick={() => navigate(`/subjects/${subject.key}`)}>{localizedSubjectLabel(subject, labelLocale)}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {books.length > 0 && (
                <>
                  <div className="subject-books-grid" ref={resultsRef}>
                    {books.map((book) => {
                      const availableCount = Number(book.available_count || 0);
                      let availabilityClass = 'subject-book-card__availability unavailable';
                      let availabilityLabel = 'Unavailable';
                      if (availableCount > 0) {
                        availabilityClass = availableCount >= 3
                          ? 'subject-book-card__availability strong'
                          : 'subject-book-card__availability limited';
                        availabilityLabel = `${availableCount} available`;
                      }

                      const isSaved = savedBookIds.has(String(book.biblio_id));
                      const matchReason = getMatchReason(book, currentQuery);

                      return (
                        <article
                          key={book.biblio_id}
                          className="subject-book-card"
                          ref={books[0]?.biblio_id === book.biblio_id ? firstResultRef : undefined}
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              handleBookOpen(book);
                            }
                          }}
                        >
                          <div className="subject-book-card__cover">
                            <button
                              type="button"
                              className="subject-book-card__open-cover"
                              onClick={() => handleBookOpen(book)}
                              aria-label={`Open details for ${book.title}`}
                            >
                              <CoverImage
                                biblioId={book.biblio_id}
                                isbn={book.isbn}
                                alt={book.title}
                                width={118}
                                height={172}
                                sizeCode="M"
                              />
                            </button>
                          </div>
                          <div className="subject-book-card__body">
                            <button type="button" className="subject-book-card__open-title" onClick={() => handleBookOpen(book)}>{book.title}</button>
                            {book.author && <p className="subject-book-card__author">{book.author}</p>}
                            {matchReason && <p className="subject-book-card__match">{matchReason}</p>}
                            <span className={availabilityClass}>{availabilityLabel}</span>
                            <p className="subject-book-card__meta">Year: {book.copyrightdate || 'N/A'}{book.editionstatement ? ` • ${book.editionstatement}` : ''}</p>
                            {book.call_number && <p className="subject-book-card__meta">Call No: {book.call_number}</p>}
                            <p className="subject-book-card__meta">Total items: {typeof book.item_count === 'number' ? book.item_count : 'N/A'}</p>
                            <div className="subject-book-card__actions">
                              <button type="button" onClick={() => handleBookOpen(book)}>View details</button>
                              <button type="button" disabled={isSaved} onClick={() => handleAddToReadingList(book)}>
                                {isSaved ? 'Saved' : 'Add to list'}
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <div className="subject-pagination">
                    <span className="subject-pagination__info">Page {currentPage} of {totalPages}</span>
                    <div className="subject-pagination__actions">
                      <button
                        type="button"
                        className="subject-pagination__button"
                        onClick={() => {
                          pendingResultScrollRef.current = true;
                          updateParams({ page: currentPage - 1 });
                        }}
                        disabled={currentPage <= 1 || booksLoading}
                      >
                        ← Previous
                      </button>
                      <button
                        type="button"
                        className="subject-pagination__button"
                        onClick={() => {
                          pendingResultScrollRef.current = true;
                          updateParams({ page: currentPage + 1 });
                        }}
                        disabled={currentPage >= totalPages || booksLoading}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default SubjectExplorerPage;